import { requestBuilder } from '../../../http/RequestBuilder';

/**
 * GET /api/admin/memberships/:membershipId/fruitful-ai/jobs?includeFinished=...
 * Agenda jobs (transaction ingest, bill jobs, etc.). Shape follows backend
 * AiServiceMembershipJobsResponse (parsed defensively on the client).
 */
export type FruitfulAiMembershipJob = Record<string, unknown>;

const parseJobsPayload = (data: unknown): FruitfulAiMembershipJob[] => {
    if (data == null) {
        return [];
    }
    if (Array.isArray(data)) {
        return data.filter(
            (x): x is FruitfulAiMembershipJob =>
                x != null && typeof x === 'object',
        );
    }
    if (
        typeof data === 'object' &&
        'jobs' in data &&
        Array.isArray((data as { jobs: unknown }).jobs)
    ) {
        const { jobs } = data as { jobs: unknown[] };
        return jobs.filter(
            (x): x is FruitfulAiMembershipJob =>
                x != null && typeof x === 'object',
        );
    }
    return [];
};

export const getFruitfulAiJobsQueryKey = ({
    membershipId,
    includeFinished,
}: {
    membershipId: string;
    includeFinished: boolean;
}): string =>
    `getFruitfulAiJobsForMembership/${membershipId}/${includeFinished}`;

const useGetFruitfulAiJobsForMembership = requestBuilder.query<
    unknown,
    { membershipId: string; includeFinished: boolean },
    Error,
    FruitfulAiMembershipJob[]
>({
    endpoint: ({ membershipId, includeFinished }) =>
        `/api/admin/memberships/${membershipId}/fruitful-ai/jobs?includeFinished=${includeFinished}`,
    queryKey: ({ membershipId, includeFinished }) =>
        getFruitfulAiJobsQueryKey({ membershipId, includeFinished }),
});

export const getJobAccountId = (
    job: FruitfulAiMembershipJob,
): string | undefined => {
    const meta =
        typeof job.metadata === 'object' && job.metadata != null
            ? (job.metadata as {
                  accountId?: unknown;
                  account_id?: unknown;
              })
            : undefined;
    const payload =
        typeof job.data === 'object' && job.data != null
            ? (job.data as {
                  accountId?: unknown;
                  account_id?: unknown;
              })
            : undefined;
    const direct =
        job.accountId ??
        job.account_id ??
        meta?.accountId ??
        meta?.account_id ??
        payload?.accountId ??
        payload?.account_id;
    if (typeof direct === 'string' && direct !== '') {
        return direct;
    }
    return undefined;
};

/**
 * Agenda jobs that look bills-related (refresh/sync, etc.). Matched on `name`, `jobType`, and
 * optional `data.jobName` / `data.kind` containing "bill" (case-insensitive). Adjust if the
 * backend renames jobs.
 */
export const isBillsRelatedFruitfulAiJob = (
    job: FruitfulAiMembershipJob,
): boolean => {
    const candidates: unknown[] = [job.name, job.jobType];
    if (typeof job.data === 'object' && job.data != null) {
        const d = job.data as { jobName?: unknown; kind?: unknown };
        candidates.push(d.jobName, d.kind);
    }
    for (const raw of candidates) {
        if (typeof raw === 'string' && raw.toLowerCase().includes('bill')) {
            return true;
        }
    }
    return false;
};

/**
 * `includeFinished` defaults to `false` (open jobs only). Pass `true` to include finished jobs.
 * Pass `enabled: false` (e.g. when the host tab is inactive) to skip the request entirely.
 */
export const useFruitfulAiJobsForMembership = (
    membershipId: string | null,
    options?: { includeFinished?: boolean; enabled?: boolean },
): {
    jobs: FruitfulAiMembershipJob[];
    isLoadingJobs: boolean;
    isJobsError: boolean;
} => {
    const includeFinished = options?.includeFinished ?? false;
    const userEnabled = options?.enabled ?? true;

    const { data, isLoading, isError } = useGetFruitfulAiJobsForMembership(
        {
            membershipId: membershipId ?? '',
            includeFinished,
        },
        {
            enabled: userEnabled && membershipId != null && membershipId !== '',
            select: (raw) => parseJobsPayload(raw),
            staleTime: 30_000,
        },
    );

    return {
        jobs: data ?? [],
        isLoadingJobs: isLoading,
        isJobsError: isError,
    };
};
